class DeliveryPromise extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.cache = {
            holidays: null,
            lastFetch: null
        };
    }

    static get observedAttributes() {
        return ['min-days', 'max-days', 'fallback-days', 'cut-off-time', 'delivery-message', 'locale', 'country'];
    }

    connectedCallback() {
        this.render();
        this.calculateDeliveryPromise();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.calculateDeliveryPromise();
        }
    }

    normalizeLocale(locale) {
        if (!locale) return 'de';
        const l = String(locale).toLowerCase();
        if (l === 'zh' || l.startsWith('zh-')) return 'zh';
        return locale;
    }

    render() {
        const locale = this.normalizeLocale(this.getAttribute('locale') || 'de');
        const loadingText = this.getLoadingText(locale);
        
        this.shadowRoot.innerHTML = `
            <style>
                .delivery-promise {
                    display: flex;
                    align-items: flex-end;
                    margin-top: 13px;
                }
                .delivery-promise p {
                    font-size: 13px;
                    font-weight: 300;
                    line-height: 12px;
                    color: #494C57;
                    margin: 0;
                }
                .delivery-promise .delivery-date {
                    font-weight: 600;
                }
            </style>
            <div class="delivery-promise">
                <p id="delivery-promise-text">${loadingText}</p>
            </div>
        `;
    }

    getLoadingText(locale) {
        const loadingTexts = {
            'en': 'Calculating delivery date...',
            'es': 'Calculando fecha de entrega...',
            'de': 'Berechne Lieferdatum...',
            'it': 'Calcolo data di consegna...',
            'zh': '正在计算预计送达日期…'
        };
        return loadingTexts[locale] || loadingTexts['de'];
    }

    getTranslations(locale) {
        return {
            'en': {
                weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                error: 'Unable to calculate delivery date'
            },
            'es': {
                weekdays: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
                months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
                error: 'No se puede calcular la fecha de entrega'
            },
            'de': {
                weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
                months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
                error: 'Lieferdatum kann nicht berechnet werden'
            },
            'it': {
                weekdays: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
                months: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
                error: 'Impossibile calcolare la data di consegna'
            },
            'zh': {
                weekdays: ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'],
                months: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'],
                error: '无法计算送达日期'
            }
        }[locale] || {
            weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
            months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
            error: 'Lieferdatum kann nicht berechnet werden'
        };
    }

    formatDate(date, weekdays, months, locale) {
        const weekday = weekdays[date.getDay()];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();

        if (locale === 'zh') {
            if (year === currentYear) {
                return `${weekday}，${date.getMonth() + 1}月${day}日`;
            }
            return `${weekday}，${year}年${date.getMonth() + 1}月${day}日`;
        }

        if (year === currentYear) {
            return weekday + ', ' + day + '. ' + month;
        } else {
            return weekday + ', ' + day + '. ' + month + ' ' + year;
        }
    }

    isWeekend(date) {
        return date.getDay() === 0 || date.getDay() === 6;
    }

    addBusinessDays(date, days) {
        const result = new Date(date);
        let addedDays = 0;
        
        while (addedDays < days) {
            result.setDate(result.getDate() + 1);
            if (!this.isWeekend(result)) {
                addedDays++;
            }
        }
        return result;
    }


    async fetchHolidays(countryCode, languageCode) {
        const HOLIDAY_API_BASE = 'https://openholidaysapi.org';
        
        if (this.cache.holidays && this.cache.lastFetch && (Date.now() - this.cache.lastFetch < 86400000)) {
            return this.cache.holidays;
        }
        
        try {
            const now = new Date();
            const startDate = new Date(now.getFullYear(), 0, 1);
            const endDate = new Date(now.getFullYear() + 1, 11, 31);
            
            const startStr = startDate.toISOString().split('T')[0];
            const endStr = endDate.toISOString().split('T')[0];
            
            const url = `${HOLIDAY_API_BASE}/PublicHolidays?countryIsoCode=${countryCode}&languageIsoCode=${languageCode}&validFrom=${startStr}&validTo=${endStr}`;
            
            const response = await fetch(url);
            if (!response.ok) throw new Error('Holiday API fetch failed');
            
            const data = await response.json();
            this.cache.holidays = data;
            this.cache.lastFetch = Date.now();
            return data;
        } catch (error) {
            console.warn('Failed to fetch holidays:', error);
            return null;
        }
    }

    isHoliday(date, holidays) {
        if (!holidays) return false;
        
        const dateStr = date.toISOString().split('T')[0];
        const isHolidayDate = holidays.some(holiday => holiday.startDate === dateStr);
        if (isHolidayDate) {
            console.log('Holiday detected for date:', dateStr);
        }
        return isHolidayDate;
    }

    calculateDeliveryDate(baseDate, daysToAdd, holidays) {
        let deliveryDate = new Date(baseDate);
        let addedDays = 0;
        
        while (addedDays < daysToAdd) {
            deliveryDate.setDate(deliveryDate.getDate() + 1);
            
            if (!this.isWeekend(deliveryDate) && !this.isHoliday(deliveryDate, holidays)) {
                addedDays++;
            }
        }
        
        return deliveryDate;
    }

    async calculateDeliveryPromise() {
        const deliveryTextElement = this.shadowRoot.getElementById('delivery-promise-text');
        if (!deliveryTextElement) return;

        try {
            const minDays = parseInt(this.getAttribute('min-days')) || 1;
            const maxDays = parseInt(this.getAttribute('max-days')) || 3;
            const fallbackDays = parseInt(this.getAttribute('fallback-days')) || 5;
            const cutOffTime = this.getAttribute('cut-off-time') || '14:00';
            const deliveryMessage = this.getAttribute('delivery-message') || '';
            const locale = this.getAttribute('locale') || 'de';

            const countryCodeMap = {
                'ES': 'ES',
                'IT': 'IT'
            };

            const languageMap = {
                'en': 'EN',
                'de': 'DE',
                'es': 'ES',
                'it': 'IT',
                'zh': 'EN'
            };

            const normalizedLocale = this.normalizeLocale(locale);
            const t = this.getTranslations(normalizedLocale);

            // Get country from the country attribute (set by Shopify localization)
            const countryCode = this.getAttribute('country') || 'DE';
            let holidays = null;
            
            // Fetch holidays for the detected country
            const holidayCountryCode = countryCodeMap[countryCode] || 'PL';
            const languageCode = languageMap[normalizedLocale] || 'EN';
            
            holidays = await this.fetchHolidays(holidayCountryCode, languageCode);
            
            // Get current date and time
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            
            // Parse cut-off time
            const [cutOffHour, cutOffMinute] = cutOffTime.split(':').map(Number);
            const currentTimeInMinutes = currentHour * 60 + currentMinute;
            const cutOffTimeInMinutes = cutOffHour * 60 + cutOffMinute;
            
            // Determine if we're past cut-off time
            const isPastCutOff = currentTimeInMinutes >= cutOffTimeInMinutes;
            
            // Calculate base delivery days based on country
            let daysToAdd = minDays;
            
            if (countryCode === 'ES') {
                daysToAdd = fallbackDays;
            } else {
                if (now.getDay() === 5 && isPastCutOff) { // Friday past cut-off
                    daysToAdd = fallbackDays;
                } else if (isPastCutOff) {
                    daysToAdd = maxDays;
                }
            }
            
            // Calculate delivery date considering holidays
            const deliveryDate = this.calculateDeliveryDate(now, daysToAdd, holidays);
            
            // Format the delivery date
            const formattedDate = this.formatDate(deliveryDate, t.weekdays, t.months, normalizedLocale);
            
            let deliveryText;
            if (deliveryMessage && deliveryMessage.trim() !== '') {
                // Replace both {date} and {data} placeholders to support different locales
                deliveryText = deliveryMessage
                    .replace('{date}', `<span class="delivery-date">${formattedDate}</span>`)
                    .replace('{data}', `<span class="delivery-date">${formattedDate}</span>`);
            } else {
                deliveryText = `<span class="delivery-date">${formattedDate}</span>`;
            }
            
            deliveryTextElement.innerHTML = deliveryText;
            
        } catch (error) {
            console.error('Error calculating delivery promise:', error);
            const locale = this.normalizeLocale(this.getAttribute('locale') || 'de');
            const t = this.getTranslations(locale);
            deliveryTextElement.textContent = t.error;
        }
    }
}

// Register the custom element
customElements.define('delivery-promise', DeliveryPromise);
